import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Send, CheckCircle, AlertCircle, Globe } from "lucide-react";
import { useForm } from "react-hook-form";
import { FaWhatsapp } from "react-icons/fa";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [submitState, setSubmitState] = useState("idle");
  const [errMsg, setErrMsg] = useState("");

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const onSubmit = async (data) => {
    setSubmitState("loading");
    try {
      await axios.post(`${API}/contact`, data);
      setSubmitState("success");
      reset();
    } catch (e) {
      setSubmitState("error");
      setErrMsg(e.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="bg-[#2D3748] py-14 px-6" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto">
          <nav className="text-xs text-gray-500 mb-4">
            <Link to="/" className="hover:text-gray-300 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-300">Contact</span>
          </nav>
          <h1 className="font-heading text-4xl lg:text-5xl font-black text-white mb-3">Get in Touch</h1>
          <p className="text-gray-400 text-base max-w-xl leading-relaxed">
            Send us your enquiry for bulk orders, product availability, or technical support. We respond within 24 hours.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-14 bg-white" data-testid="contact-main">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-10">
            {/* Info Cards */}
            <div className="space-y-4">
              <div className="p-5 bg-[#F3F4F6] border border-gray-200 hover:border-[#0F62FE] transition-colors" data-testid="contact-address-card">
                <MapPin size={20} className="text-[#0F62FE] mb-2.5" />
                <h3 className="font-heading font-bold text-[#111827] text-sm mb-2">Office Address</h3>
                <p className="text-[#4B5563] text-sm leading-relaxed">
                  Shop No. 22, Chenoy Trade Center,<br />
                  Park Lane, Kalasiguda,<br />
                  Secunderabad – 500003,<br />
                  Telangana, India
                </p>
              </div>

              <div className="p-5 bg-[#F3F4F6] border border-gray-200 hover:border-[#0F62FE] transition-colors" data-testid="contact-phone-card">
                <Phone size={20} className="text-[#0F62FE] mb-2.5" />
                <h3 className="font-heading font-bold text-[#111827] text-sm mb-2">Phone</h3>
                <a href="tel:9908131050" data-testid="contact-phone1" className="block text-[#4B5563] hover:text-[#0F62FE] text-sm transition-colors">+91 9908131050</a>
                <a href="tel:8500914557" data-testid="contact-phone2" className="block text-[#4B5563] hover:text-[#0F62FE] text-sm transition-colors mt-1">+91 8500914557</a>
              </div>

              <div className="p-5 bg-[#F3F4F6] border border-gray-200 hover:border-[#0F62FE] transition-colors" data-testid="contact-email-card">
                <Mail size={20} className="text-[#0F62FE] mb-2.5" />
                <h3 className="font-heading font-bold text-[#111827] text-sm mb-2">Email</h3>
                <a href="mailto:jaininfotech22@gmail.com" data-testid="contact-email-link" className="text-[#4B5563] hover:text-[#0F62FE] text-sm transition-colors break-all">
                  jaininfotech22@gmail.com
                </a>
              </div>

              <div className="p-5 bg-[#F3F4F6] border border-gray-200 hover:border-[#0F62FE] transition-colors" data-testid="contact-website-card">
                <Globe size={20} className="text-[#0F62FE] mb-2.5" />
                <h3 className="font-heading font-bold text-[#111827] text-sm mb-2">Website</h3>
                <a
                  href="https://www.jaininfotech.co.in"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-website-link"
                  className="text-[#4B5563] hover:text-[#0F62FE] text-sm transition-colors break-all"
                >
                  www.jaininfotech.co.in
                </a>
              </div>
              <div className="p-5 bg-[#F3F4F6] border border-gray-200 hover:border-[#0F62FE] transition-colors" data-testid="contact-hours-card">
                <Clock size={20} className="text-[#0F62FE] mb-2.5" />
                <h3 className="font-heading font-bold text-[#111827] text-sm mb-2">Business Hours</h3>
                <p className="text-[#4B5563] text-sm">Mon–Sat: 9:00 AM – 7:00 PM</p>
                <p className="text-[#4B5563] text-sm">Sunday: Closed</p>
              </div>

              <a
                href="https://wa.me/918500914557?text=Hi%2C%20I%20would%20like%20to%20get%20a%20quote%20for%20IT%20products"
                target="_blank" rel="noreferrer"
                data-testid="contact-whatsapp-btn"
                className="flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#1fb655] text-white font-semibold py-3.5 transition-colors"
              >
                <FaWhatsapp size={18} /> Chat on WhatsApp
              </a>
            </div>

            {/* Form */}
            <div className="lg:col-span-2" data-testid="contact-form-panel">
              <div className="p-7 bg-white border border-gray-200 shadow-sm">
                <h2 className="font-heading font-bold text-[#111827] text-xl mb-6">Send an Enquiry</h2>

                {submitState === "success" ? (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    className="flex items-start gap-4 p-6 bg-green-50 border border-green-200"
                    data-testid="contact-success-message"
                  >
                    <CheckCircle size={24} className="text-green-600 shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-bold text-green-800 mb-1">Enquiry Submitted!</h3>
                      <p className="text-green-700 text-sm">Thank you for reaching out. We'll get back to you within 24 hours at your provided email or phone.</p>
                      <button onClick={() => setSubmitState("idle")} className="mt-3 text-xs text-green-700 underline hover:no-underline">
                        Send another message
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-[#374151] mb-1.5 uppercase tracking-wide">Full Name *</label>
                        <input
                          {...register("name", { required: "Name is required", minLength: { value: 2, message: "Name too short" } })}
                          data-testid="contact-name-input"
                          type="text"
                          placeholder="Rajesh Kumar"
                          className={`w-full px-4 py-3 border text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent transition-all ${errors.name ? "border-red-400" : "border-gray-300"}`}
                        />
                        {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#374151] mb-1.5 uppercase tracking-wide">Email Address *</label>
                        <input
                          {...register("email", { required: "Email is required", pattern: { value: /\S+@\S+\.\S+/, message: "Invalid email address" } })}
                          data-testid="contact-email-input"
                          type="email"
                          placeholder="rajesh@company.com"
                          className={`w-full px-4 py-3 border text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent transition-all ${errors.email ? "border-red-400" : "border-gray-300"}`}
                        />
                        {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-[#374151] mb-1.5 uppercase tracking-wide">Phone Number *</label>
                        <input
                          {...register("phone", { required: "Phone is required" })}
                          data-testid="contact-phone-input"
                          type="tel"
                          placeholder="+91 98765 43210"
                          className={`w-full px-4 py-3 border text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent transition-all ${errors.phone ? "border-red-400" : "border-gray-300"}`}
                        />
                        {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#374151] mb-1.5 uppercase tracking-wide">Company Name</label>
                        <input
                          {...register("company")}
                          data-testid="contact-company-input"
                          type="text"
                          placeholder="Your Company Pvt. Ltd."
                          className="w-full px-4 py-3 border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent transition-all"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-[#374151] mb-1.5 uppercase tracking-wide">Product / Subject</label>
                      <select
                        {...register("subject")}
                        data-testid="contact-subject-select"
                        className="w-full px-4 py-3 border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent transition-all bg-white"
                      >
                        <option value="">Select a product category</option>
                        <option>Cables & Connectivity</option>
                        <option>Peripherals & Audio</option>
                        <option>Networking Infrastructure</option>
                        <option>CCTV & Surveillance</option>
                        <option>KVM & Enterprise Solutions</option>
                        <option>General Inquiry</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-[#374151] mb-1.5 uppercase tracking-wide">Message *</label>
                      <textarea
                        {...register("message", { required: "Message is required", minLength: { value: 10, message: "Please write at least 10 characters" } })}
                        data-testid="contact-message-input"
                        rows={5}
                        placeholder="Tell us about your requirements — product type, quantity, delivery location..."
                        className={`w-full px-4 py-3 border text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent transition-all resize-none ${errors.message ? "border-red-400" : "border-gray-300"}`}
                      />
                      {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message.message}</p>}
                    </div>

                    {submitState === "error" && (
                      <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="contact-error-message">
                        <AlertCircle size={16} /> {errMsg}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitState === "loading"}
                      data-testid="contact-submit-btn"
                      className="w-full flex items-center justify-center gap-2 bg-[#0F62FE] hover:bg-[#0052CC] disabled:bg-gray-400 text-white font-semibold py-3.5 transition-all duration-200 hover:shadow-lg text-sm"
                    >
                      {submitState === "loading" ? (
                        <><span className="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4" /> Sending...</>
                      ) : (
                        <><Send size={16} /> Send Enquiry</>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps */}
      <section className="bg-[#F3F4F6] py-12" data-testid="maps-section">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-heading text-2xl font-black text-[#111827] mb-5">Find Our Office</h2>
          <div className="w-full h-[420px] border border-gray-200 overflow-hidden shadow-sm" data-testid="google-maps-embed">
            <iframe
              title="Jain Infotech Office - Chenoy Trade Center, Secunderabad"
              src="https://maps.google.com/maps?q=Chenoy+Trade+Center+Park+Lane+Kalasiguda+Secunderabad+500003&output=embed&z=16"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <p className="mt-3 text-sm text-[#4B5563] flex items-center gap-2">
            <MapPin size={14} className="text-[#0F62FE] shrink-0" />
            Shop No. 22, Chenoy Trade Center, Park Lane, Kalasiguda, Secunderabad – 500003, Telangana, India
          </p>
        </div>
      </section>
    </div>
  );
}
